import { Check, EyeOff, ShieldCheck, Trash2, UserX, X } from "lucide-react";
import type { ReactNode } from "react";

// Honesty section: who Revetra is the right tool for, who it isn't, and
// the security posture. Deliberately turns people away when something
// else (a lawyer, a billing office, an advocate) is the real answer.
interface FitItem {
  marker: string;
  body: ReactNode;
}

const GOOD: FitItem[] = [
  {
    marker: "01",
    body: (
      <>
        You received a written denial or prior-auth refusal from a U.S. health insurer in the{" "}
        <strong className="font-medium text-ink">last 180 days</strong>.
      </>
    ),
  },
  {
    marker: "02",
    body: (
      <>
        The denial is about{" "}
        <strong className="font-medium text-ink">
          medical necessity, coverage criteria, or prior authorization
        </strong>{" "}
        — not eligibility or premium issues.
      </>
    ),
  },
  {
    marker: "03",
    body: (
      <>
        You can find or describe the{" "}
        <strong className="font-medium text-ink">clinical context</strong> — what was prescribed,
        ordered, or performed, and roughly why.
      </>
    ),
  },
  {
    marker: "04",
    body: (
      <>
        You want a <strong className="font-medium text-ink">first-pass internal appeal</strong> —
        Level 1, signed by you, sent to your insurer.
      </>
    ),
  },
];

const BAD: FitItem[] = [
  {
    marker: "01",
    body: (
      <>
        You&apos;ve already exhausted{" "}
        <strong className="font-medium text-ink">internal and external appeals</strong> and are
        considering litigation — that&apos;s a lawyer&apos;s job.
      </>
    ),
  },
  {
    marker: "02",
    body: (
      <>
        You need <strong className="font-medium text-ink">medical advice</strong>. We don&apos;t
        diagnose, recommend treatment, or replace your physician.
      </>
    ),
  },
  {
    marker: "03",
    body: (
      <>
        Your insurer is denying you{" "}
        <strong className="font-medium text-ink">coverage entirely</strong> (loss of eligibility) —
        that&apos;s a different process and we&apos;ll point you to it.
      </>
    ),
  },
];

const SECURITY = [
  {
    icon: ShieldCheck,
    title: "HIPAA-aware",
    desc: "Encrypted in transit and at rest. Access logged. Built to the HIPAA Security Rule's technical safeguards.",
  },
  {
    icon: Trash2,
    title: "Deleted within 24 hours",
    desc: "Your denial letter, medical records, and drafts are automatically deleted within 24 hours, on a fixed schedule. Closing the tab clears your browser session immediately.",
  },
  {
    icon: UserX,
    title: "No account required",
    desc: "You don't need to sign up to get the free analysis. Email is only required if you want to download or restore a draft.",
  },
  {
    icon: EyeOff,
    title: "Never sold or shared",
    desc: "We don't sell data. We don't share with payers. Your documents and letters never train AI — only anonymized outcomes ever inform the service.",
  },
];

function FitColumn({
  variant,
  title,
  items,
}: {
  variant: "good" | "bad";
  title: string;
  items: FitItem[];
}) {
  const accent = variant === "good" ? "text-forest" : "text-brick";
  return (
    <div className="border-line px-9 py-8 max-[980px]:[&:first-child]:border-b-[0.5px] min-[981px]:[&:first-child]:border-r-[0.5px]">
      <h3 className="mb-[18px] flex items-center gap-2.5 font-serif text-[18px] font-medium tracking-[-0.005em] text-ink">
        {variant === "good" ? (
          <Check size={18} className={accent} aria-hidden />
        ) : (
          <X size={18} className={accent} aria-hidden />
        )}
        {title}
      </h3>
      <ul className="m-0 list-none p-0">
        {items.map((item) => (
          <li
            key={item.marker}
            className="flex items-start gap-2.5 border-t-[0.5px] border-line py-[11px] text-[14px] leading-[1.55] text-ink-soft [&:first-child]:border-t-0"
          >
            <span className={`mt-1 w-8 flex-shrink-0 font-mono text-[10px] tracking-[0.06em] ${accent}`}>
              {item.marker}
            </span>
            <span>{item.body}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TrustHonesty() {
  return (
    <section
      className="border-y-[0.5px] border-line bg-surface px-6 py-28 max-[980px]:py-[72px] sm:px-8"
      aria-label="What we are and what we are not"
    >
      <div className="mx-auto max-w-[1180px]">
        <div className="lp-reveal mx-auto mb-16 max-w-[640px] text-center">
          <span className="eyebrow justify-center after:inline-block after:h-px after:w-[18px] after:bg-current after:content-['']">
            Before you upload anything
          </span>
          <h2 className="mt-[18px] font-serif text-[38px] font-normal leading-[1.15] tracking-[-0.018em] text-ink max-[980px]:text-[28px]">
            Whether Revetra is right for you
          </h2>
          <p className="mt-4 text-[15.5px] leading-[1.6] text-ink-muted">
            We&apos;re not for everyone. Below is when Revetra is the right tool — and when something
            else (a lawyer, your provider&apos;s billing office, an advocate) is what you actually
            need.
          </p>
        </div>

        <div className="lp-reveal mb-16 grid grid-cols-1 border-y-[0.5px] border-line min-[981px]:grid-cols-2">
          <FitColumn variant="good" title="Revetra is right when" items={GOOD} />
          <FitColumn variant="bad" title="Revetra is the wrong tool when" items={BAD} />
        </div>

        <div className="lp-reveal grid grid-cols-4 border-y-[0.5px] border-line max-[980px]:grid-cols-2 max-[640px]:grid-cols-1">
          {SECURITY.map((cell) => {
            const Icon = cell.icon;
            return (
              <div
                key={cell.title}
                className="border-r-[0.5px] border-line px-[22px] py-6 [&:last-child]:border-r-0"
              >
                <span className="mb-3 inline-flex h-7 w-7 items-center justify-center text-ink">
                  <Icon size={18} strokeWidth={1.75} aria-hidden />
                </span>
                <h4 className="mb-1 font-serif text-[14.5px] font-medium tracking-[-0.005em] text-ink">
                  {cell.title}
                </h4>
                <p className="text-[12.5px] leading-[1.55] text-ink-muted">{cell.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
